'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import TopBar from '@/components/TopBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const benefits = [
  {
    title: 'Single Point of Contact',
    description: 'One dedicated manager handles every lender, every document, and every deadline. You focus on your business — we manage the loan.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    title: 'Higher Approval Rates',
    description: 'Our systematic approach to application preparation — clean financials, complete documentation, proactive issue resolution — consistently delivers higher approval rates.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'Faster Processing Times',
    description: 'We know what lenders need before they ask for it. Pre-emptive documentation and established lender relationships cut processing time by weeks.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'Compliance & Risk Management',
    description: 'We review every document for compliance issues, covenant violations, and risk factors before they reach the lender — protecting your approval and your relationship.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    title: 'Portfolio-Level Oversight',
    description: 'For businesses with multiple loans, we provide portfolio-level management — tracking maturities, covenant compliance, and refinancing opportunities across all facilities.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
  },
  {
    title: 'Lender Relationship Management',
    description: 'We maintain active relationships with your lenders — so when issues arise, we resolve them through established channels before they become problems.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Loan Intake & Review',
    description: 'We review your existing loan portfolio or new loan application — identifying gaps, risks, and opportunities before the process begins.',
  },
  {
    step: '02',
    title: 'Documentation Preparation',
    description: 'We compile and organize all required documentation: financial statements, tax returns, business plans, collateral schedules, and legal agreements.',
  },
  {
    step: '03',
    title: 'Underwriting Support',
    description: 'We work alongside the lender\'s underwriting team, answering questions, providing supplemental data, and resolving conditions promptly.',
  },
  {
    step: '04',title: 'Compliance Review',description: 'Every document is reviewed for regulatory compliance, covenant alignment, and risk exposure before submission or execution.',
  },
  {
    step: '05',title: 'Closing Coordination',description: 'We coordinate all closing parties — attorneys, title companies, appraisers, and lenders — to ensure a smooth, on-schedule closing.',
  },
  {
    step: '06',title: 'Post-Close Monitoring',description: 'After closing, we monitor covenant compliance, track reporting deadlines, and flag refinancing opportunities as market conditions evolve.',
  },
];

const loanTypes = [
  'SBA 7(a) & 504 Loans',
  'Commercial Real Estate Loans',
  'Equipment Financing',
  'Lines of Credit',
  'Bridge & Construction Loans',
  'USDA Business Loans',
  'Mezzanine Financing',
  'Syndicated Loans',
];

function AnimatedSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </div>
  );
}

export default function BusinessLoanManagementPage() {
  return (
    <main className="min-h-screen font-sans bg-white">
      <TopBar />
      <Header />

      {/* Hero */}
      <section className="relative w-full min-h-[480px] flex items-center justify-center overflow-hidden">
        <Image
          src="/assets/images/istockphoto-495640504-612x612-1787084581411.jpg"
          alt="Two businessmen reviewing loan documents and financial paperwork"
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-brand-darkbg/75" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-24">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-brand-green" />
            <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Service 04</span>
            <div className="h-px w-12 bg-brand-green" />
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-medium text-white mb-6 leading-tight">
            Business Loan Management
          </h1>
          <p className="font-sans text-gray-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            End-to-end loan management from application to closing — and beyond. We handle every detail so your deal closes on time, every time.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-green text-white rounded-md font-sans font-semibold text-sm hover:bg-green-600 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-green/30"
            >
              Start Your Application
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              href="/#services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white rounded-md font-sans font-semibold text-sm hover:bg-white/10 transition-all duration-200"
            >
              All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Deep Dive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-0.5 bg-brand-green" />
                <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">What We Do</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-navy mb-6">
                The Loan Process Is Complex. We Make It Simple.
              </h2>
              <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
                Commercial lending involves dozens of moving parts — lenders, attorneys, appraisers, title companies, underwriters, compliance officers. Most deals that fall apart do so not because of the deal itself, but because of process failures: missing documents, missed deadlines, unresolved conditions.
              </p>
              <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
                Grid Global's loan management service puts a dedicated professional in charge of every aspect of your loan process. We act as your advocate and project manager simultaneously — keeping every party accountable, every document current, and every deadline met.
              </p>
              <p className="font-sans text-gray-600 text-base leading-relaxed">
                From SBA loans and commercial real estate financing to equipment loans and syndicated facilities, we manage the full spectrum of commercial lending with the same systematic precision.
              </p>
            </AnimatedSection>
            <AnimatedSection className="grid grid-cols-2 gap-4">
              {loanTypes.map((loan, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-brand-green/30 hover:bg-brand-green/5 transition-all duration-200">
                  <div className="w-2 h-2 rounded-full bg-brand-green flex-shrink-0" />
                  <span className="font-sans text-sm text-brand-navy font-medium">{loan}</span>
                </div>
              ))}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-brand-darkbg">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-brand-green" />
              <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Why Grid Global</span>
              <div className="h-px w-12 bg-brand-green" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-white">
              The Advantages of Working With Us
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <AnimatedSection key={i} className="p-6 rounded-2xl bg-brand-altbg border border-white/10 hover:border-brand-green/40 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center text-brand-green mb-5 group-hover:bg-brand-green group-hover:text-white transition-all duration-300">
                  {benefit.icon}
                </div>
                <h3 className="font-serif text-lg font-medium text-white mb-3">{benefit.title}</h3>
                <p className="font-sans text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-brand-green" />
              <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Our Process</span>
              <div className="h-px w-12 bg-brand-green" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-navy">
              How We Manage Your Loan End-to-End
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, i) => (
              <AnimatedSection key={i}>
                <div className="p-8 rounded-2xl border border-gray-100 hover:border-brand-navy/20 hover:shadow-lg transition-all duration-300 h-full">
                  <div className="font-serif text-5xl font-medium text-brand-navy/10 mb-4 leading-none">{step.step}</div>
                  <h3 className="font-serif text-xl font-medium text-brand-navy mb-3">{step.title}</h3>
                  <p className="font-sans text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-white mb-5">
              Let Us Manage Your Next Loan From Start to Close
            </h2>
            <p className="font-sans text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
              Tell us about your loan needs. We'll take it from there — handling every detail so you can focus on running your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-green text-white rounded-md font-sans font-semibold text-sm hover:bg-green-600 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-green/30"
              >
                Get Started Today
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                href="/#services"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white rounded-md font-sans font-semibold text-sm hover:bg-white/10 transition-all duration-200"
              >
                Explore Other Services
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </main>
  );
}
